import { useState, useEffect } from "react";
import { FaUserFriends, FaCalendar } from "react-icons/fa";
import { FaLocationDot } from "react-icons/fa6";
import { BiSolidCategory } from "react-icons/bi";
import { IoShareSocialSharp } from "react-icons/io5";
import { CiGlobe } from "react-icons/ci";
import { getAvailableWikiData } from "../../../api/wiki";
import LoadingSpinner from "../../Universal/LoadingSpinner";
import { SECTION_CONFIG } from "./constants";
import SplitBackground from "./SplitBackground";

const Overview = (props) => {
  const [overviewData, setOverviewData] = useState(null);
  const [visible, setVisible] = useState(false);

  const pad = Math.max(props.baseMargin, 24);

  useEffect(() => {
    setOverviewData(null);
    setVisible(false);
    getAvailableWikiData(props.currentCase)
      .then((response) => {
        if (!response || !Array.isArray(response)) {
          setOverviewData({});
          return;
        }
        const items = {};
        response.forEach((item) => {
          items[item.sub_section] = item.data;
        });
        setOverviewData(items);
        requestAnimationFrame(() =>
          requestAnimationFrame(() => setVisible(true)),
        );
      })
      .catch(() => setOverviewData({}));
  }, [props.currentCase]);

  if (!overviewData) {
    return (
      <div
        className="flex items-center justify-center"
        style={{
          height: "100%",
          marginTop: 24,
          paddingLeft: pad,
          paddingRight: pad,
        }}
      >
        <LoadingSpinner />
      </div>
    );
  }

  const overview = overviewData.Overview || {};

  const infoItems = [
    {
      icon: FaCalendar,
      label: `Founded in ${overview.founded_year}`,
      show: typeof overview.founded_year === "number" && overview.founded_year,
    },
    {
      icon: FaUserFriends,
      label:
        typeof overview.company_size === "object" && overview.company_size
          ? `${overview.company_size[0]} – ${overview.company_size[1]} employees`
          : "",
      show: typeof overview.company_size === "object" && overview.company_size,
    },
    {
      icon: FaLocationDot,
      label:
        typeof overview.hq === "object" && overview.hq
          ? `${overview.hq.city}, ${overview.hq.state}`
          : "",
      show: typeof overview.hq === "object" && overview.hq,
    },
    {
      icon: BiSolidCategory,
      label: overview.industry,
      show: typeof overview.industry === "string" && overview.industry,
    },
    {
      icon: IoShareSocialSharp,
      label:
        typeof overview.follower_count === "number"
          ? `${overview.follower_count.toLocaleString()} followers`
          : "",
      show:
        typeof overview.follower_count === "number" && overview.follower_count,
    },
    {
      icon: CiGlobe,
      label: overview.website,
      show: typeof overview.website === "string" && overview.website,
    },
  ].filter((i) => i.show);

  // Animation only — colors/borders/shadow via className
  const animStyle = (delay = 0) => ({
    transition: `all 0.5s ease-out ${delay}ms`,
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(16px)",
  });

  const availableSections = SECTION_CONFIG.filter(
    ({ key }) => overviewData[key],
  );

  return (
    <div
      className="font-poppins font-normal"
      style={{
        marginTop: 24,
        paddingBottom: 32,
        paddingLeft: pad,
        paddingRight: pad,
      }}
    >
      <div className="flex flex-col gap-5">
        {/* Top row */}
        <div className="flex gap-5">
          {/* Description */}
          <div
            className="rounded-lg border border-surfaceBorder bg-surface/80 p-6 shadow-login-panel backdrop-blur-md"
            style={{ ...animStyle(0), flex: 2 }}
          >
            {typeof overview.description === "string" &&
            overview.description ? (
              <p
                className="text-sm leading-relaxed text-mainText"
                style={{ margin: 0 }}
              >
                {overview.description}
              </p>
            ) : (
              <p
                className="text-xs italic text-mutedText"
                style={{ margin: 0 }}
              >
                No company description available
              </p>
            )}
          </div>

          {/* Info panel */}
          <div
            className="rounded-lg border border-surfaceBorder bg-surface/80 p-6 shadow-login-panel backdrop-blur-md"
            style={{ ...animStyle(75), flex: 1 }}
          >
            <div className="flex flex-col gap-4">
              {infoItems.length > 0 ? (
                infoItems.map(({ icon: Icon, label }, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-innerBg">
                      <Icon
                        aria-hidden
                        style={{ width: 14, height: 14 }}
                        className="text-velBlue"
                      />
                    </div>
                    <span className="text-sm text-mainText">{label}</span>
                  </div>
                ))
              ) : (
                <p
                  className="text-xs italic text-mutedText"
                  style={{ margin: 0 }}
                >
                  No company details available
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Section cards */}
        {availableSections.length > 0 && (
          <div
            style={{
              display: "grid",
              gap: 16,
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            }}
          >
            {availableSections.map(({ key, image, color, valueKey }, index) => {
              const data = overviewData[key];
              const getValue = () => {
                if (valueKey === "items") return undefined;
                if (valueKey === "count") return data.count;
                if (valueKey === "length") return data.length;
                return "";
              };
              return (
                <div
                  key={key}
                  style={{
                    transition: `all 0.5s ease-out ${150 + index * 60}ms`,
                    opacity: visible ? 1 : 0,
                    transform: visible ? "translateY(0)" : "translateY(20px)",
                  }}
                >
                  <SplitBackground
                    imageUrl={image}
                    color={color}
                    title={key}
                    value={getValue()}
                    items={valueKey === "items" ? data : undefined}
                    setCurrentSubSection={props.setCurrentSubSection}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Overview;
